import React from "react";

// On-brand SVG visuals for the ARC landing page. No external assets/deps.
const NAVY = "#0B1F3A";
const SLATE = "#6C8A9B";
const ACCENT = "#185FA5";
const GREEN = "#3B6D11";
const BORDER = "#D9E0E6";
const TEXT = "#2D3748";

// ── Hero: sources → ARC engine (reconcile + validate) → evidence/results ──
export function HeroPipeline() {
  // chip vertical centres + the distinct points each connector lands on the engine
  const sources = [
    { cy: 70, label: "Databases", land: 132 },
    { cy: 156, label: "Files & reports", land: 172 },
    { cy: 242, label: "Operational data", land: 212 },
  ];
  return (
    <svg
      viewBox="0 0 600 320"
      className="arc-svg arc-hero-svg"
      role="img"
      aria-label="ARC reconciles and validates many sources into evidence and results"
    >
      <defs>
        <marker
          id="arc-arrow"
          markerWidth="7"
          markerHeight="7"
          refX="5.5"
          refY="3"
          orient="auto"
        >
          <path d="M0,0 L6,3 L0,6 Z" fill={SLATE} />
        </marker>
      </defs>

      {/* connectors: sources → engine (behind the chips) */}
      {sources.map((s) => (
        <path
          key={`c-${s.label}`}
          d={`M196 ${s.cy} C 228 ${s.cy} 222 ${s.land} 250 ${s.land}`}
          fill="none"
          stroke={SLATE}
          strokeWidth="1.6"
          opacity="0.55"
          markerEnd="url(#arc-arrow)"
        />
      ))}

      {/* sources */}
      {sources.map((s) => (
        <g key={s.label}>
          <rect
            x="0"
            y={s.cy - 29}
            width="196"
            height="58"
            rx="12"
            fill="#fff"
            stroke={BORDER}
          />
          <circle cx="26" cy={s.cy} r="6" fill={ACCENT} />
          <text x="46" y={s.cy + 5} fontSize="13.5" fill={TEXT}>
            {s.label}
          </text>
        </g>
      ))}

      {/* engine */}
      <rect x="250" y="74" width="150" height="196" rx="16" fill={NAVY} />
      <text
        x="325"
        y="104"
        fontSize="11.5"
        letterSpacing="1"
        fill="#9FB3C8"
        textAnchor="middle"
      >
        ARC ENGINE
      </text>
      <rect x="270" y="126" width="110" height="46" rx="10" fill={ACCENT} />
      <text x="325" y="154" fontSize="13.5" fill="#fff" textAnchor="middle">
        Reconcile
      </text>
      <rect
        x="270"
        y="186"
        width="110"
        height="46"
        rx="10"
        fill="rgba(255,255,255,0.10)"
        stroke="rgba(255,255,255,0.22)"
      />
      <text x="325" y="214" fontSize="13.5" fill="#fff" textAnchor="middle">
        Validate
      </text>

      {/* connectors: engine → outputs */}
      <path
        d="M400 149 C 426 149 422 138 448 138"
        fill="none"
        stroke={SLATE}
        strokeWidth="1.6"
        opacity="0.55"
        markerEnd="url(#arc-arrow)"
      />
      <path
        d="M400 209 C 426 209 422 224 448 224"
        fill="none"
        stroke={SLATE}
        strokeWidth="1.6"
        opacity="0.55"
        markerEnd="url(#arc-arrow)"
      />

      {/* outputs */}
      <rect x="448" y="110" width="138" height="56" rx="12" fill="#EAF3DE" />
      <path
        d="M470 138 l6 6 l12 -13"
        fill="none"
        stroke={GREEN}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <text x="498" y="143" fontSize="13.5" fill={GREEN}>
        Evidence
      </text>

      <rect
        x="448"
        y="196"
        width="138"
        height="56"
        rx="12"
        fill="#fff"
        stroke={BORDER}
      />
      <text x="468" y="218" fontSize="11" fill={SLATE}>
        Results
      </text>
      <rect x="468" y="227" width="66" height="7" rx="3.5" fill={GREEN} />
      <rect x="468" y="239" width="40" height="7" rx="3.5" fill="#C9CED6" />
    </svg>
  );
}

// ── Challenge: repeated effort vs compounding reusable capability ──
export function EffortVsCapability() {
  return (
    <svg
      viewBox="0 0 560 260"
      className="arc-svg"
      role="img"
      aria-label="Traditional delivery repeats effort; ARC compounds reusable capability"
    >
      {/* left: repeated flat effort */}
      <text x="20" y="28" fontSize="13" fill={SLATE} fontWeight="600">
        Traditional delivery
      </text>
      {[0, 1, 2, 3].map((i) => (
        <g key={i}>
          <rect
            x={20 + i * 56}
            y="70"
            width="40"
            height="120"
            rx="6"
            fill="#E3E8ED"
          />
          <text
            x={40 + i * 56}
            y="210"
            fontSize="11"
            fill={SLATE}
            textAnchor="middle"
          >
            R{i + 1}
          </text>
        </g>
      ))}
      <text x="20" y="240" fontSize="12" fill={SLATE}>
        Effort repeated every release
      </text>

      {/* divider */}
      <line x1="280" y1="20" x2="280" y2="240" stroke={BORDER} />

      {/* right: compounding capability */}
      <text x="320" y="28" fontSize="13" fill={NAVY} fontWeight="600">
        ARC
      </text>
      {[0, 1, 2, 3].map((i) => {
        const h = 50 + i * 36;
        return (
          <g key={i}>
            <rect
              x={320 + i * 56}
              y={190 - h}
              width="40"
              height={h}
              rx="6"
              fill={i === 3 ? ACCENT : "#BBD0E8"}
            />
            <text
              x={340 + i * 56}
              y="210"
              fontSize="11"
              fill={SLATE}
              textAnchor="middle"
            >
              R{i + 1}
            </text>
          </g>
        );
      })}
      <path
        d="M340 140 L396 122 L452 96 L508 60"
        fill="none"
        stroke={GREEN}
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <text x="320" y="240" fontSize="12" fill={NAVY}>
        Capability compounds over time
      </text>
    </svg>
  );
}

// ── Mini master-suite workflow (matches the in-app node/arrow look) ──
export function MiniWorkflow() {
  const nodes = [
    { label: "Extract", color: SLATE },
    { label: "Reconcile", color: ACCENT },
    { label: "Validate", color: GREEN },
    { label: "Evidence", color: NAVY },
  ];
  return (
    <svg
      viewBox="0 0 520 96"
      className="arc-svg arc-miniflow-svg"
      role="img"
      aria-label="Master suite workflow: extract, reconcile, validate, evidence"
    >
      {nodes.map((n, i) => {
        const x = 8 + i * 128;
        return (
          <g key={n.label}>
            <rect
              x={x}
              y="22"
              width="104"
              height="52"
              rx="10"
              fill="#fff"
              stroke={BORDER}
            />
            <rect x={x} y="22" width="5" height="52" rx="2.5" fill={n.color} />
            <circle cx={x + 88} cy="36" r="4" fill={n.color} />
            <text x={x + 18} y="53" fontSize="13" fill={TEXT}>
              {n.label}
            </text>
            {i < nodes.length - 1 && (
              <line
                x1={x + 104}
                y1="48"
                x2={x + 128}
                y2="48"
                stroke={SLATE}
                strokeWidth="1.5"
              />
            )}
          </g>
        );
      })}
    </svg>
  );
}
